import { useEffect, useMemo, useState } from 'react'

import { MultiSelect, SimpleGrid } from '@mantine/core'
import { useGetList, useTranslate } from 'react-admin'
import { PromotionOperationStatusApiDTO } from '../../codegen/generated/Api.schemas'
import { VideoPromotionHelper } from '../../helpers/VideoPromotion.helper'
import { YoutubeChannel } from '../../types/YoutubeChannel'
import { IVideoPromotionStaticFilters } from '../../types/components/VideoPromotion.type'

type VideosPromotionsOperationsFiltersProps = {
  isLoading: boolean
  onUpdate: (staticFilters: IVideoPromotionStaticFilters) => void
}

export const VideosPromotionsOperationsFilters = ({
  onUpdate,
  isLoading,
}: VideosPromotionsOperationsFiltersProps) => {
  const t = useTranslate()

  const [youtubeChannelIds, setYoutubeChannelIds] = useState<YoutubeChannel['id'][] | undefined>()
  const [statuses, setStatuses] = useState<PromotionOperationStatusApiDTO[] | undefined>()

  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })
  const youtubeChannels = useMemo(
    () =>
      (youtubeChannelsQuery.data ?? []).map(b => ({
        value: b.id,
        label: b.name,
      })),
    [youtubeChannelsQuery.data],
  )

  const statusesItems = useMemo(
    () =>
      VideoPromotionHelper.STATUSES.map(ps => ({
        ...ps,
        value: ps.id as PromotionOperationStatusApiDTO,
        label: t(`pages.videoPromotion.statuses.${ps.id}`, { defaultValue: ps.id }),
      })),
    [t],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: onUpdate breaks cycle
  useEffect(() => {
    const state = {
      ...(youtubeChannelIds ? { youtubeChannelIds } : undefined),
      ...(statuses ? { statuses } : undefined),
    }

    onUpdate(state)
  }, [youtubeChannelIds, statuses])

  return (
    <SimpleGrid cols={4} mt="xl" mb="sm">
      <MultiSelect
        label={t('pages.youtubeChannels._title')}
        data={youtubeChannels}
        value={youtubeChannelIds}
        onChange={setYoutubeChannelIds}
        searchable
        clearable
        disabled={isLoading}
        autoComplete="off"
        data-form-type="other"
      />
      <MultiSelect
        label={t('_generics.status')}
        data={statusesItems}
        value={statuses}
        onChange={s => setStatuses(s as PromotionOperationStatusApiDTO[])}
        searchable
        clearable
        disabled={isLoading}
        autoComplete="off"
        data-form-type="other"
      />
    </SimpleGrid>
  )
}
